import type { StoryTimePlacement } from '../types/timeline';
import type { useWorkspaceStore } from '../stores/workspace';
import type { TimelineOccurrence } from '../types/timeline';

export function useTimelinePlanningActions(store: ReturnType<typeof useWorkspaceStore>) {
async function addEntityToTimeline(entityId: string, input: Partial<TimelineOccurrence> & { sortKey: number }) {
  try {
    await store.saveTimelineOccurrence(entityId, {
      eraEntityId: input.eraEntityId ?? null,
      inUniverseStart: input.inUniverseStart ?? null,
      inUniverseEnd: input.inUniverseEnd ?? null,
      sortKey: input.sortKey,
      narrativePosition: input.narrativePosition ?? null,
      precision: input.precision ?? 'exact',
      isUncertain: input.isUncertain ?? false,
      notes: input.notes ?? null
    });
  } catch (cause) { store.error = cause instanceof Error ? cause.message : 'Entity konnte nicht zur Timeline hinzugefügt werden.'; }
}
async function createEntityFromTimeline(request: { templateId: string; title: string; eraEntityId: string | null; sortKey: number; addToTimeline: boolean }) {
  try {
    const entity = await store.createEntity(request.templateId, request.title, {}, undefined, [], false);
    if (entity && request.addToTimeline) await addEntityToTimeline(entity.id, { eraEntityId: request.eraEntityId, sortKey: request.sortKey });
    await store.loadEntities();
    await store.loadTimeline();
  } catch (cause) { store.error = cause instanceof Error ? cause.message : 'Entity konnte nicht erstellt werden.'; }
}
async function updateTimelineFromView(occurrenceId: string, input: Partial<TimelineOccurrence>) {
  try { await store.updateTimelineOccurrence(occurrenceId, input); }
  catch (cause) { store.error = cause instanceof Error ? cause.message : 'Timeline-Eintrag konnte nicht gespeichert werden.'; }
}
async function saveTimelinePlanning(request: { entityId: string; occurrenceId: string | null; sortKey: number; input: Partial<TimelineOccurrence> }, done: (error?: string) => void) {
  try {
    if (request.occurrenceId) await store.updateTimelineOccurrence(request.occurrenceId, request.input);
    else await store.saveTimelineOccurrence(request.entityId, {
      eraEntityId: null, inUniverseStart: null, inUniverseEnd: null, sortKey: request.sortKey,
      narrativePosition: null, precision: 'exact', isUncertain: false, notes: null,
      ...request.input
    });
    done();
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'Zeit konnte nicht gespeichert werden.';
    store.error = message; done(message);
  }
}
async function saveTimelineSegmentPlanning(request: { segmentId: string; input?: Partial<StoryTimePlacement>; durationMinutes?: number | null }, done: (error?: string) => void) {
  try {
    if (request.input) await store.updateTimelineSegmentTimePlacement(request.segmentId, request.input);
    if (request.durationMinutes !== undefined) await store.updateTimelineSegmentDuration(request.segmentId, request.durationMinutes);
    done();
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'Segmentzeit konnte nicht gespeichert werden.';
    store.error = message; done(message);
  }
}
async function fillMissingTimelineSegmentTimes(steps: Array<{ segmentId: string; timeSource: 'inherit' | 'relative'; relativeAnchor: 'scene-start' | 'previous-segment-end' | null }>, done: (error?: string) => void) {
  try { await store.fillMissingTimelineSegmentTimes(steps); done(); }
  catch (cause) {
    const message = cause instanceof Error ? cause.message : 'Segmentzeiten konnten nicht vollständig verknüpft werden.';
    store.error = message; done(message);
  }
}
async function removeTimelineFromView(entityId: string) {
  try { await store.deleteTimelineOccurrence(entityId); }
  catch (cause) { store.error = cause instanceof Error ? cause.message : 'Timeline-Eintrag konnte nicht entfernt werden.'; }
}
async function reorderTimelineFromView(updates: Array<{ occurrenceId: string; eraEntityId: string | null; sortKey: number }>) {
  try { await store.reorderTimelineOccurrences(updates); }
  catch (cause) { store.error = cause instanceof Error ? cause.message : 'Timeline-Reihenfolge konnte nicht gespeichert werden.'; }
}
async function reorderTimelineErasFromView(eraEntityIds: string[]) {
  try { await store.reorderTimelineEras(eraEntityIds); }
  catch (cause) { store.error = cause instanceof Error ? cause.message : 'Epochenreihenfolge konnte nicht gespeichert werden.'; }
}
  return { addEntityToTimeline, createEntityFromTimeline, updateTimelineFromView, saveTimelinePlanning, saveTimelineSegmentPlanning, fillMissingTimelineSegmentTimes, removeTimelineFromView, reorderTimelineFromView, reorderTimelineErasFromView };
}
